import useSheetAnim from '../hooks/useSheetAnim';

/**
 * FormSheet — the app's one full-screen dialog shell.
 *
 * Same anatomy as SaleNotesSheet (fixed overlay on --bg, a surface header with a
 * chevron-down close, a scrolling body, and an optional sticky footer), pulled out
 * so Receipts, Documents and anything after them don't each hand-roll it. Full
 * height rather than a bottom sheet: these forms run to eight fields, and the
 * on-screen keyboard eats roughly half a phone.
 *
 * Renders nothing when `open` is false, so callers can mount it unconditionally.
 * Rises from the bottom edge and slides back down on close.
 *
 * `zIndex` defaults to the app's sheet layer (4500). Raise it only for a sheet
 * opened FROM a picker (SheetSelect sits at 4900) so the new sheet rises OVER the
 * closing one instead of behind it — keep it under 5000 (update prompt) and 6000
 * (AntD popups), or confirms raised from inside it land underneath.
 */
export default function FormSheet({ open, title, sub, onClose, footer, children, zIndex = 4500 }) {
  const { mounted, closing } = useSheetAnim(open);
  if (!mounted) return null;

  return (
    <div className={`sheet-panel${closing ? ' is-closing' : ''}`} style={{ position: 'fixed', inset: 0, zIndex, background: 'var(--bg)', display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px', paddingTop: 'calc(10px + env(safe-area-inset-top))', background: 'var(--surface)', borderBottom: '1px solid var(--border)', flex: 'none' }}>
        <div
          onClick={onClose}
          role="button"
          tabIndex={0}
          aria-label="Close"
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClose?.(); } }}
          style={{ width: 36, height: 36, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flex: 'none' }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600 }}>{title}</div>
          {sub && <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>{sub}</div>}
        </div>
      </div>

      <div className="scrx" style={{ flex: 1, overflowY: 'auto', padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
        {children}
      </div>

      {footer && (
        <div style={{ display: 'flex', gap: 10, padding: '12px 12px max(12px, env(safe-area-inset-bottom))', background: 'var(--surface)', borderTop: '1px solid var(--border)', flex: 'none' }}>
          {footer}
        </div>
      )}
    </div>
  );
}
